/**
 * AccountSettings — signup-and-billing Phase 1 Session 6.
 *
 * Replaces the prior 25-line static read-only `INFO_ROWS` map with a
 * Panel that shows:
 *   - Read-only metadata (email, ARC ID, status)
 *   - 6 editable profile fields collected from the wizard (firm_name, role,
 *     asset_classes, markets, focus, volume)
 *   - Single Save button — sends only dirty fields via PATCH /api/users/me
 *   - Saved/Error toast feedback
 *
 * Settings edits NEVER set onboarding_completed=true (that flag flips only
 * via the wizard), so re-saving after approval does NOT re-fire the
 * APPROVAL_REQUESTED lifecycle event. Server enforces this same invariant
 * defensively.
 */

import { useCallback, useEffect, useState, type ReactElement } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { Panel, Button } from '@/components/ui';
import { useAuth } from '@/hooks/use-auth';
import { useUserProfile, usePatchUserProfile } from '@/hooks/use-user-profile';
import { resetWelcomeTour } from '@/components/onboarding';
import type { UserProfilePatch } from '@arcanum-hq/shared-types';

type EditableField =
  | 'firm_name'
  // `work_role` is the wire-shape field (renamed from `role` 2026-05-25 to
  // eliminate field-name collision with the JWT admin claim). User-facing
  // label below stays "Role" — only the internal identifier changed.
  | 'work_role'
  | 'asset_classes'
  | 'markets'
  | 'focus'
  | 'volume';

interface FieldConfig {
  field: EditableField;
  label: string;
  placeholder: string;
}

const FIELDS: readonly FieldConfig[] = [
  { field: 'firm_name', label: 'Firm', placeholder: 'e.g. Arcanum Capital' },
  {
    field: 'work_role',
    label: 'Role',
    placeholder: 'e.g. Operator, Developer...',
  },
  {
    field: 'asset_classes',
    label: 'Asset Classes',
    placeholder: 'e.g. Multifamily, Industrial...',
  },
  {
    field: 'markets',
    label: 'Markets',
    placeholder: 'e.g. Atlanta, Dallas...',
  },
  { field: 'focus', label: 'Focus', placeholder: 'e.g. Mostly value-add...' },
  { field: 'volume', label: 'Volume', placeholder: 'e.g. $200M portfolio...' },
] as const;

function StaticRow({
  label,
  value,
}: {
  label: string;
  value: string;
}): ReactElement {
  return (
    <div className="flex items-center gap-2" data-testid="account-row">
      <span className="text-sm text-text-muted font-mono w-36 shrink-0">
        {label}
      </span>
      <span className="text-sm text-text-bright font-mono">{value}</span>
    </div>
  );
}

export function AccountSettings(): ReactElement {
  const { user } = useAuth();
  const { profile, isLoading, isError } = useUserProfile();
  const patch = usePatchUserProfile();
  const navigate = useNavigate();
  const [drafts, setDrafts] = useState<Partial<Record<EditableField, string>>>(
    {}
  );
  const [savedAt, setSavedAt] = useState<number | null>(null);

  // Replay tour handler — clears the localStorage flag then navigates to
  // /cockpit to re-arm __root.tsx's auto-trigger useEffect. Acceptable v1
  // trade-off: this navigates AWAY from /settings; any unsaved draft is
  // lost. v1.1 nicety: dispatch a window event listened-for in __root.tsx
  // to toggle showTour from any route without nav. signup-billing-mvp
  // Phase 3 Session 3 (2026-05-13).
  const handleReplayTour = useCallback(() => {
    resetWelcomeTour(user?.id);
    void navigate({ to: '/cockpit' });
  }, [navigate, user?.id]);

  // Hydrate drafts from profile on first load (and re-hydrate on profile change).
  useEffect(() => {
    if (profile) {
      setDrafts({
        firm_name: profile.firm_name ?? '',
        work_role: profile.work_role ?? '',
        asset_classes: profile.asset_classes ?? '',
        markets: profile.markets ?? '',
        focus: profile.focus ?? '',
        volume: profile.volume ?? '',
      });
    }
  }, [profile]);

  if (isLoading) {
    return (
      <Panel title="Account">
        <div className="text-sm text-text-muted font-mono py-2">
          Loading profile…
        </div>
      </Panel>
    );
  }

  if (isError || !profile) {
    return (
      <Panel title="Account">
        <div className="text-sm text-danger font-mono py-2" role="alert">
          Failed to load profile. Refresh to retry.
        </div>
      </Panel>
    );
  }

  // Build the diff of dirty fields (empty strings stay empty — no validation
  // here beyond a 2-char min on submit).
  const dirty: UserProfilePatch = {};
  for (const { field } of FIELDS) {
    const current = drafts[field] ?? '';
    const original = profile[field] ?? '';
    if (current !== original) {
      // Send the trimmed value, but only if it meets the 2-char min OR is the
      // explicit-clear pattern. Server-side userProfilePatchSchema enforces
      // min 2 chars, so trying to send "A" or "" returns 400 — gate here.
      const trimmed = current.trim();
      if (trimmed.length >= 2) {
        dirty[field] = trimmed;
      }
    }
  }

  const hasDirty = Object.keys(dirty).length > 0;

  async function handleSave() {
    if (!hasDirty || patch.isPending) return;
    setSavedAt(null);
    try {
      await patch.mutateAsync(dirty);
      setSavedAt(Date.now());
    } catch {
      // Error surfaced via patch.error; user retries.
    }
  }

  return (
    <Panel title="Account">
      <div className="flex flex-col gap-2">
        {/* Read-only metadata */}
        <StaticRow label="Email" value={user?.email ?? '—'} />
        <StaticRow label="ARC ID" value={profile.arc_id ?? '—'} />
        <StaticRow label="Status" value={profile.status} />

        <div className="border-t border-panel-border my-2" />

        {/* Editable profile fields */}
        {FIELDS.map(({ field, label, placeholder }) => (
          <div
            key={field}
            className="flex items-center gap-2"
            data-testid={`account-field-${field}`}
          >
            <label
              htmlFor={`account-field-${field}`}
              className="text-sm text-text-muted font-mono w-36 shrink-0"
            >
              {label}
            </label>
            <input
              id={`account-field-${field}`}
              type="text"
              value={drafts[field] ?? ''}
              onChange={(e) =>
                setDrafts({ ...drafts, [field]: e.target.value })
              }
              placeholder={placeholder}
              disabled={patch.isPending}
              className="flex-1 px-3 py-2 bg-input-bg border border-input-border rounded-[3px] text-sm text-text-bright font-body outline-none focus:border-accent/30 disabled:opacity-50"
            />
          </div>
        ))}

        {/* Save action */}
        <div className="flex items-center gap-3 mt-2">
          <Button
            variant="primary"
            disabled={!hasDirty || patch.isPending}
            onClick={handleSave}
            data-testid="account-save"
          >
            {patch.isPending ? 'Saving…' : 'Save'}
          </Button>

          {savedAt && !patch.isPending && (
            <span
              className="text-xs text-accent font-mono"
              data-testid="account-saved-toast"
              role="status"
            >
              Saved
            </span>
          )}

          {patch.error && (
            <span
              className="text-xs text-danger font-mono"
              data-testid="account-save-error"
              role="alert"
            >
              {patch.error.message}
            </span>
          )}
        </div>

        {/* Replay welcome tour — signup-billing-mvp Phase 3 Session 3 */}
        <div
          className="flex items-center gap-3 mt-3 pt-3 border-t border-panel-border"
          data-testid="account-replay-tour-row"
        >
          <div className="flex-1">
            <div className="text-sm text-text">Replay welcome tour</div>
            <div className="text-xs text-text-muted">
              Take the product tour again. Opens on the cockpit.
            </div>
          </div>
          <Button
            variant="secondary"
            onClick={handleReplayTour}
            data-testid="account-replay-tour-button"
          >
            Replay tour
          </Button>
        </div>
      </div>
    </Panel>
  );
}
